import { memo, useCallback } from 'react';
import { Reply, Trash2 } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { EmojiReactionPicker } from '@/components/messages/EmojiReactionPicker';
import type { IMessageActionsProps } from '@/@types/messages';

export const TelegramMessageActions = memo(function TelegramMessageActions({
  message,
  onReaction,
  onDeleteMessage,
  onReplyMessage,
  children,
}: IMessageActionsProps) {
  const handleReaction = useCallback(
    (emoji: string) => {
      if (message.platformMessageId !== undefined && onReaction) {
        onReaction(message.platformMessageId, emoji);
      }
    },
    [message.platformMessageId, onReaction]
  );

  const handleReply = useCallback(() => {
    onReplyMessage?.(message);
  }, [message, onReplyMessage]);

  const handleDeleteForMe = useCallback(() => {
    if (message.platformMessageId !== undefined && onDeleteMessage) {
      onDeleteMessage(message.platformMessageId, false);
    }
  }, [message.platformMessageId, onDeleteMessage]);

  const handleDeleteForEveryone = useCallback(() => {
    if (message.platformMessageId !== undefined && onDeleteMessage) {
      onDeleteMessage(message.platformMessageId, true);
    }
  }, [message.platformMessageId, onDeleteMessage]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div>{children}</div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        {/* Emoji reaction row */}
        <div className="px-1">
          <EmojiReactionPicker onSelect={handleReaction} />
        </div>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleReply}>
          <Reply className="mr-2 size-4" />
          Reply
        </ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Trash2 className="mr-2 size-4 text-destructive" />
            <span className="text-destructive">Delete</span>
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem onClick={handleDeleteForMe}>Delete for me</ContextMenuItem>
            {message.isOutgoing && (
              <ContextMenuItem
                onClick={handleDeleteForEveryone}
                className="text-destructive focus-visible:text-destructive"
              >
                Delete for everyone
              </ContextMenuItem>
            )}
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenuContent>
    </ContextMenu>
  );
});
